// Claims a file the splitter funnel (riffloop.app) stashed in the browser,
// without any server round-trip. riffloop.app and studio.riffloop.app are the
// SAME site, so a hidden bridge iframe on riffloop.app can read the funnel's
// IndexedDB and hand the blob back over postMessage. See
// cinematic/site/public/handoff-bridge.html.

const isLocal = /^(localhost|127\.0\.0\.1)$/.test(location.hostname);
// In dev, run the cinematic site (Vite) on :5174 so it doesn't collide with the
// dashboard on :5173. In prod the bridge is served from the cinematic site.
const BRIDGE_ORIGIN = isLocal ? 'http://localhost:5174' : 'https://riffloop.app';
const BRIDGE_URL = `${BRIDGE_ORIGIN}/handoff-bridge.html`;

interface HandoffResponse {
  // `type` is the message type; the blob's MIME is sent separately as `mime`.
  type: string;
  id: string;
  ok?: boolean;
  name?: string;
  mime?: string;
  buffer?: ArrayBuffer;
}

export function claimHandoff(id: string, timeoutMs = 20000): Promise<File | null> {
  return new Promise((resolve) => {
    let settled = false;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    let timer = 0;

    const finish = (result: File | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMsg);
      clearTimeout(timer);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      resolve(result);
    };

    const request = () => {
      iframe.contentWindow?.postMessage({ type: 'riffloop-handoff-request', id }, BRIDGE_ORIGIN);
    };

    const onMsg = (e: MessageEvent) => {
      if (e.origin !== BRIDGE_ORIGIN) return;
      const d = e.data as HandoffResponse | undefined;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'riffloop-handoff-ready') {
        request();
        return;
      }
      if (d.type === 'riffloop-handoff-response' && d.id === id) {
        if (d.ok && d.buffer) {
          finish(new File([d.buffer], d.name || 'track', { type: d.mime || 'audio/mpeg' }));
        } else {
          finish(null);
        }
      }
    };

    window.addEventListener('message', onMsg);
    iframe.onload = () => request(); // belt-and-suspenders if the ready ping is missed
    iframe.src = BRIDGE_URL;
    document.body.appendChild(iframe);
    timer = window.setTimeout(() => finish(null), timeoutMs);
  });
}
